import type {
	CourseSelections,
	ResolvedSelectionEntry,
} from '@/components/prescriptions/utils/prescriptionSelectionRules'
import {
	getCourseOptionGroups,
	getSelectorForFlatSlot,
	formatWorkLabel,
	isElectiveCourse,
	isStudiesVariant,
	resolveCourseSelections,
	resolveCourseWithElective,
	stripTitleHtml,
} from '@/components/prescriptions/utils/prescriptionSelectionRules'
import type { PrescriptionSelectionCourse } from '@/types/fetchPrescriptionSelection'

export const PRESCRIPTION_GA_EVENT = {
	listView: 'prescriptions_view_list_view',
	optionsView: 'prescriptions_view_options_view',
	tool1CourseCompletion: 'prescriptions_tool_1_course_completion',
	tool1Download: 'prescriptions_tool_1_download',
	tool2ListDownload: 'prescriptions_tool_2_list_download',
} as const

// `page_url` (pathname) + `page_query` (search without the leading `?`).
export const getGAPageContext = (): {
	page_url: string
	page_query: string
} => {
	if (typeof window === 'undefined') return { page_url: '', page_query: '' }
	return {
		page_url: window.location.pathname,
		page_query: window.location.search.replace(/^\?/, ''),
	}
}

export interface GASelection {
	focus_area_title: string
	prescription_option: string
	selection_option: string
	prescription_type: string
	prescription_form: string
}

export interface GACourse {
	course_title: string
	syllabus: string
	selections: GASelection[]
	ga_build_error?: boolean
}

const STUDIES_FOCUS_AREA_FALLBACK = 'Author and authority'

const logGAFailure = (
	stage: string,
	courseTitle: string,
	error: unknown,
): void => {
	// eslint-disable-next-line no-console
	console.error(
		`[prescription GA] ${stage} failed for "${courseTitle}"`,
		error,
	)
}

const asOptionName = (option: unknown): string =>
	typeof option === 'string' ? option.trim() : ''

/** Same reasoning for work metadata: `.toLowerCase()` throws on a non-string. */
const toLowerString = (value: unknown): string =>
	typeof value === 'string' ? value.toLowerCase() : ''

/**
 * The option a course's selections belong to, verbatim as shown in the
 * dropdown (e.g. "Elective 2: Historical worlds"). Empty when the course has
 * no options, or when an elective course has no elective picked yet.
 *
 * Mirrors `buildPrescriptionPrintPayload`, which resolves the option cmsid from
 * option group [0] for the studies variant and from `electiveOptionIndex` for
 * the elective variant — so GA and the print payload can never disagree about
 * which option a selection belongs to.
 */
const getGAPrescriptionOption = (
	course: PrescriptionSelectionCourse,
	electiveOptionIndex: number | undefined,
): string => {
	const optionGroups = getCourseOptionGroups(course)
	if (optionGroups.length === 0) return ''

	// Studies is checked first, matching `PrescriptionSelectionTool` and
	// `buildPrescriptionPrintPayload`.
	if (isStudiesVariant(course)) {
		return asOptionName(optionGroups[0]?.option)
	}

	if (isElectiveCourse(course)) {
		if (electiveOptionIndex === undefined) return ''
		return asOptionName(optionGroups[electiveOptionIndex]?.option)
	}

	return ''
}

const buildGASelection = (
	entry: ResolvedSelectionEntry,
	resolvedCourse: PrescriptionSelectionCourse,
	studiesFallback: string,
	prescriptionOption: string,
): GASelection => ({
	focus_area_title:
		getSelectorForFlatSlot(resolvedCourse, entry.slotIndex)?.[
			'focus-area-title'
		] ?? studiesFallback,
	prescription_option: prescriptionOption,
	selection_option: formatWorkLabel(
		entry.work.author,
		stripTitleHtml(entry.work.title),
	),
	prescription_type: toLowerString(entry.work['prescription-type']),
	prescription_form: toLowerString(entry.work['prescription-form']),
})

export const buildGACourse = (
	course: PrescriptionSelectionCourse,
	courseSelections: CourseSelections | undefined,
	electiveOptionIndex: number | undefined,
): GACourse => {
	const courseTitle = course?.['course-title'] ?? ''
	const syllabus = course?.syllabus ?? ''

	try {
		const resolvedCourse = isElectiveCourse(course)
			? resolveCourseWithElective(course, electiveOptionIndex)
			: course

		const entries = resolveCourseSelections(
			resolvedCourse,
			courseSelections,
		)
		const studiesFallback = isStudiesVariant(course)
			? STUDIES_FOCUS_AREA_FALLBACK
			: ''

		// Course-level, so resolved once rather than per selection. Read from the
		let prescriptionOption = ''
		let optionFailed = false
		try {
			prescriptionOption = getGAPrescriptionOption(
				course,
				electiveOptionIndex,
			)
		} catch (error) {
			optionFailed = true
			logGAFailure('option lookup', courseTitle, error)
		}

		const selections: GASelection[] = []
		let failedEntries = 0
		entries.forEach((entry) => {
			try {
				selections.push(
					buildGASelection(
						entry,
						resolvedCourse,
						studiesFallback,
						prescriptionOption,
					),
				)
			} catch (error) {
				failedEntries += 1
				logGAFailure('selection', courseTitle, error)
			}
		})

		const degraded = optionFailed || failedEntries > 0
		return {
			course_title: course['course-title'],
			syllabus: course.syllabus ?? '',
			selections,
			...(degraded ? { ga_build_error: true } : {}),
		}
	} catch (error) {
		logGAFailure('course', courseTitle, error)
		return {
			course_title: courseTitle,
			syllabus,
			selections: [],
			ga_build_error: true,
		}
	}
}
